import { Component, Inject, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { ConfigStateService } from '@abp/ng.core';
import { OAuthService } from 'angular-oauth2-oidc';
import { firstValueFrom } from 'rxjs';
import { ClientService } from '@proxy/clients';
import { SommelierService } from '@proxy/sommeliers';
import { environment } from 'src/environments/environment';
import { BiometricAuthService } from '../shared/services/biometric-auth.service';

@Component({
  selector: 'app-google-callback',
  standalone: false,
  template: `
    <div class="flex items-center justify-center min-h-screen bg-white">
      <div class="text-center text-metro-blue-secondary">
        <div class="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-metro-blue-secondary"></div>
        <p>{{ errorMessage || 'Cargando...' }}</p>
      </div>
    </div>
  `,
})
export class GoogleCallbackComponent implements OnInit {
  errorMessage = '';

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private oauth: OAuthService,
    private config: ConfigStateService,
    private router: Router,
    private sommelierService: SommelierService,
    private clientService: ClientService,
    private biometric: BiometricAuthService
  ) {}

  async ngOnInit(): Promise<void> {
    const callbackUrl = `${environment.application.baseUrl}/auth/google-callback`;

    this.oauth.configure({
      ...environment.oAuthConfig,
      redirectUri: callbackUrl,
      responseType: 'code',
    });

    const hash = this.doc.location.hash || '';

    try {
      await this.oauth.loadDiscoveryDocument();

      if (hash.includes('external=done')) {
        this.doc.location.hash = '';
        this.oauth.initCodeFlow();
        return;
      }

      await this.oauth.tryLoginCodeFlow();

      if (!this.oauth.hasValidAccessToken()) {
        throw new Error('No se pudo completar el login con Google.');
      }

      await firstValueFrom(this.config.refreshAppState());

      // Registrar biometría ANTES de navegar (para que el componente no se destruya)
      if (this.biometric.shouldPromptRegistration()) {
        try { await this.biometric.register(); } catch { /* usuario canceló */ }
      }

      // Persistir estado biométrico para unlock futuro
      if (this.biometric.hasCredential()) {
        localStorage.setItem('wp_biometric_session_active', 'true');
      }
      // Marcar que este usuario ingresa por Google (para re-redirigir automáticamente al expirar)
      localStorage.setItem('wp_login_provider', 'google');
      const refreshToken = this.oauth.getRefreshToken();
      if (refreshToken) {
        localStorage.setItem('wp_refresh_token', refreshToken);
      }

      await this.navigateAfterLogin();
    } catch (error) {
      console.error('Error procesando login con Google:', error);
      this.errorMessage = 'No se pudo iniciar sesion con Google.';
      setTimeout(() => this.router.navigate(['/account/login'], { replaceUrl: true }), 1500);
    }
  }

  private async navigateAfterLogin(): Promise<void> {
    const currentUser = await firstValueFrom(this.config.getOne$('currentUser'));
    const roles = currentUser?.roles ?? [];

    if (roles.includes('sommelier')) {
      const slug = await firstValueFrom(this.sommelierService.getMySlug());
      await this.router.navigate([`/${slug}`], { replaceUrl: true });
      return;
    }

    if (roles.includes('client')) {
      const slug = await firstValueFrom(this.clientService.getSommelierSlug());
      await this.router.navigate([`/${slug}`], { replaceUrl: true });
      return;
    }

    await this.router.navigate(['/'], { replaceUrl: true });
  }
}
